import { useLingui } from "@lingui/react/macro";
import {
	type PaymentAttempt,
	PaymentFormError,
	decidePaymentRecovery,
} from "@offstreetparking/portal-flow/components/CreatePermitWizard/components/PaymentForm";
import { offstreetClient } from "~/services/offstreetApi";
import type { RouterOutputs } from "~/services/offstreetApi/types";
import { useAuthorizeNet } from "../../hooks/useAuthorizeNet/useAuthorizeNet";
type AuthorizeNetFormProps = {
	paymentSession: NonNullable<RouterOutputs["paymentSession"]["createPaymentSessionPublic"]["session"]>;
	formId: string;
	onError: (error?: {
		message: string;
		authorizeNetDeclineCode?: string;
	}) => void;
	onRetryableError: (error: PaymentFormError) => void;
	onSuccess: (publicIdentifier: string) => void;
	setIsProcessing: (isProcessing: boolean) => void;
};
export const AuthorizeNetForm = ({
	paymentSession,
	formId,
	onSuccess,
	onError,
	onRetryableError,
	setIsProcessing,
}: AuthorizeNetFormProps) => {
	const { t } = useLingui();
	const { mutate: acknowledgeFailedPayment } = offstreetClient.paymentSession.acknowledgeFailedPayment.useMutation();
	const recover = (attempt: PaymentAttempt, publicIdentifier?: string) => {
		const { outcome, shouldAcknowledge } = decidePaymentRecovery(attempt);
		if (outcome === "succeeded") {
			onSuccess(publicIdentifier ?? "");
		} else {
			if (shouldAcknowledge) {
				acknowledgeFailedPayment({ paymentId: paymentSession.paymentId });
			}
			if (outcome === "unconfirmed") {
				onRetryableError(PaymentFormError.UNCONFIRMED);
			} else {
				const declineCode = attempt.type === "completed" ? attempt.declineCode : undefined;
				onError({
					message:
						declineCode && attempt.type === "completed"
							? (attempt.reason ?? t`Payment error`)
							: t`Payment error`,
					authorizeNetDeclineCode: declineCode,
				});
			}
		}
		setIsProcessing(false);
	};
	const { mutate: completePaymentSession } = offstreetClient.paymentSession.completePaymentSession.useMutation({
		onSuccess: (data) =>
			recover(
				{
					type: "completed",
					status: data.status,
					declineCode: data.status === "failed" ? data.declineCode : undefined,
					reason: data.status === "failed" ? data.reason : undefined,
				},
				data.status === "succeeded" ? data.publicIdentifier : undefined,
			),
		onError: () => recover({ type: "request-failed" }),
	});
	const { isReady, openLightbox } = useAuthorizeNet({
		paymentSession,
		callbacks: {
			onApproval: (nonce) => {
				setIsProcessing(true);
				completePaymentSession({
					paymentSessionId: paymentSession.paymentId,
					token: nonce,
				});
			},
			onError: () => recover({ type: "not-charged" }),
		},
	});
	const handleWizardSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!isReady) {
			return;
		}
		openLightbox();
	};
	return <form id={formId} onSubmit={handleWizardSubmit} />;
};
